import React, { useState } from 'react';
import { ArrowLeft, Save, Trophy, AlertTriangle } from 'lucide-react';
import { doc, collection, addDoc, updateDoc, increment, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FIRESTORE_PATHS } from '../../config/appConfig';
import { useUser } from '../../contexts/UserContext';
import { getCorrectiveDrill, injectDrillIntoPlan } from '../../utils/planner';
import logger from '../../utils/logger';

const MatchLogger = ({ userProfile, onSave, onCancel }) => {
    const { user } = useUser();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [showInjectionModal, setShowInjectionModal] = useState(false);
    const [pendingDrill, setPendingDrill] = useState(null);
    const [data, setData] = useState({
        format: '',
        result: '',
        runs: '',
        ballsFaced: '',
        wickets: '',
        overs: '',
        dismissalType: '',
        shotAttempted: '',
        feeling: '',
        deliveryType: ''
    });

    const updateData = (key, value) => {
        setData({ ...data, [key]: value });
    };

    const [error, setError] = useState(null);

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            // Sanitize Integers
            const sanitizedData = {
                ...data,
                runs: parseInt(data.runs) || 0,
                ballsFaced: parseInt(data.ballsFaced) || 0,
                wickets: parseInt(data.wickets) || 0,
                overs: parseFloat(data.overs) || 0,
            };

            // 1. Ensure TrainingProfile/current exists first
            const trainingProfileRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_PROFILE.BASE, FIRESTORE_PATHS.TRAINING_PROFILE.CURRENT);
            const profileDoc = await getDoc(trainingProfileRef);
            if (!profileDoc.exists()) {
                await setDoc(trainingProfileRef, {
                    grindScore: 0,
                    totalXp: 0,
                    role: userProfile?.role || '',
                    weeklyPlan: userProfile?.weeklyPlan || []
                });
            }
            
            // 2. Create Log Entry — flattened path: Users/{userId}/TrainingMatches/{matchId}
            // Compute summary at write time (VRCricket SessionSummary pattern)
            const strikeRate = sanitizedData.ballsFaced > 0
                ? parseFloat(((sanitizedData.runs / sanitizedData.ballsFaced) * 100).toFixed(2))
                : 0;
            const isOut = sanitizedData.dismissalType && sanitizedData.dismissalType !== 'Not Out' && sanitizedData.dismissalType !== 'Retired';

            const matchesRef = collection(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_MATCHES);
            const logRef = await addDoc(matchesRef, {
                date: new Date().toISOString(),
                type: 'match',
                data: sanitizedData,
                xpGained: 50,
                summary: {
                    runs: sanitizedData.runs,
                    ballsFaced: sanitizedData.ballsFaced,
                    strikeRate,
                    format: sanitizedData.format,
                    result: sanitizedData.result,
                    isOut,
                    dismissalType: sanitizedData.dismissalType || null
                }
            });

            // 3. Update User Stats — flattened paths
            const trainingStatsRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.STATS.BASE, FIRESTORE_PATHS.STATS.WEB_TRAINING);

            await updateDoc(trainingProfileRef, {
                grindScore: increment(5),
                totalXp: increment(50)
            });

            // Initialize stats if doesn't exist, then update
            const statsDoc = await getDoc(trainingStatsRef);
            if (!statsDoc.exists()) {
                await setDoc(trainingStatsRef, {
                    matchesPlayed: 0,
                    totalRuns: 0,
                    totalBallsFaced: 0,
                    inningsBatted: 0,
                    notOuts: 0,
                    wicketsTaken: 0,
                    oversBowled: 0,
                    trainingSessions: 0
                });
            }
            
            await updateDoc(trainingStatsRef, {
                // Aggregated Performance Stats (For Instant Averages)
                matchesPlayed: increment(1),
                totalRuns: increment(sanitizedData.runs),
                totalBallsFaced: increment(sanitizedData.ballsFaced),
                inningsBatted: increment(sanitizedData.ballsFaced > 0 ? 1 : 0), // Basic heuristic
                notOuts: increment(isOut ? 0 : 1),

                // Bowling Stats
                wicketsTaken: increment(sanitizedData.wickets),
                oversBowled: increment(sanitizedData.overs)
            });

            // 3. Trigger Recommendation Engine (New Logic)
            const drill = getCorrectiveDrill({ type: 'match', data: data }, userProfile || {});

            if (drill) {
                setPendingDrill(drill);
                setShowInjectionModal(true);
                // Do not navigate yet
            } else {
                onSave(); // Return to dashboard immediately
            }

        } catch (error) {
            logger.error('MatchLogger', 'Error saving match log:', error);
            setError(`Failed to save: ${error.code || error.message}. Check console/permissions.`);
            setLoading(false); // Only stop loading if error, otherwise keep loading while modal shows
        }
    };

    const handleConfirmInjection = async () => {
        try {
            const currentPlan = userProfile.weeklyPlan || [];
            const newPlan = injectDrillIntoPlan(currentPlan, pendingDrill);

            // Organized structure: Users/{userId}/Training/profile
            const trainingProfileRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_PROFILE.BASE, FIRESTORE_PATHS.TRAINING_PROFILE.CURRENT);
            await updateDoc(trainingProfileRef, {
                weeklyPlan: newPlan
            });
            onSave();
        } catch (error) {
            logger.error('MatchLogger', 'Error updating plan:', error);
            onSave(); // Proceed anyway
        }
    };

    const renderStep1 = () => (
        <div className="space-y-6">
            <div>
                <label className="text-gray-400 text-sm block mb-2">Format</label>
                <div className="flex flex-wrap gap-2">
                    {['T20', 'ODI', 'Test', 'Box/Indoor', 'Gully'].map(f => (
                        <button
                            key={f}
                            onClick={() => updateData('format', f)}
                            className={`px-4 py-2 rounded-full border ${data.format === f ? 'bg-white text-black border-white' : 'bg-transparent border-gray-600 text-gray-400'}`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <label className="text-gray-400 text-sm block mb-2">Result</label>
                <div className="flex gap-2">
                    {['Won', 'Lost', 'Draw', 'Abandoned'].map(r => (
                        <button
                            key={r}
                            onClick={() => updateData('result', r)}
                            className={`flex-1 py-3 rounded-lg border font-bold ${data.result === r
                                ? r === 'Won' ? 'bg-green-500/20 text-green-400 border-green-500' : 'bg-red-500/20 text-red-400 border-red-500'
                                : 'bg-gray-800 border-gray-700 text-gray-400'
                                }`}
                        >
                            {r}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-gray-400 text-sm block mb-2">Runs Scored</label>
                    <input
                        type="number"
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white text-2xl font-mono"
                        placeholder="0"
                        value={data.runs}
                        onChange={(e) => updateData('runs', e.target.value)}
                    />
                </div>
                <div>
                    <label className="text-gray-400 text-sm block mb-2">Balls Faced</label>
                    <input
                        type="number"
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white text-2xl font-mono"
                        placeholder="0"
                        value={data.ballsFaced}
                        onChange={(e) => updateData('ballsFaced', e.target.value)}
                    />
                </div>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-6">
            <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl">
                <h3 className="text-red-400 font-bold flex items-center mb-4"><AlertTriangle size={18} className="mr-2" /> The Deep Dive</h3>

                <div className="space-y-4">
                    <div>
                        <label className="text-gray-400 text-xs uppercase block mb-2">How did you get out?</label>
                        <select
                            className="w-full bg-black border border-gray-700 rounded-lg p-3 text-white"
                            value={data.dismissalType}
                            onChange={(e) => updateData('dismissalType', e.target.value)}
                        >
                            <option value="">Select Dismissal...</option>
                            <option value="Not Out">Not Out</option>
                            <option value="Bowled">Bowled</option>
                            <option value="LBW">LBW</option>
                            <option value="Caught (Slip)">Caught (Slip)</option>
                            <option value="Caught (Keeper)">Caught (Keeper)</option>
                            <option value="Caught (Boundary)">Caught (Boundary)</option>
                            <option value="Caught (Circle)">Caught (Circle)</option>
                            <option value="Stumped">Stumped</option>
                            <option value="Run Out">Run Out</option>
                        </select>
                    </div>

                    {data.dismissalType !== 'Not Out' && data.dismissalType && (
                        <div className="animate-fadeIn">
                            <label className="text-gray-400 text-xs uppercase block mb-2">What shot were you trying?</label>
                            <select
                                className="w-full bg-black border border-gray-700 rounded-lg p-3 text-white"
                                value={data.shotAttempted}
                                onChange={(e) => updateData('shotAttempted', e.target.value)}
                            >
                                <option value="">Select Shot...</option>
                                <optgroup label="Vertical Bat">
                                    <option value="Defense">Defense</option>
                                    <option value="Drive">Drive (Cover/Straight)</option>
                                    <option value="Flick">Flick</option>
                                </optgroup>
                                <optgroup label="Horizontal Bat">
                                    <option value="Pull">Pull</option>
                                    <option value="Cut">Cut</option>
                                    <option value="Sweep">Sweep</option>
                                </optgroup>
                                <optgroup label="Aggressive/Risk">
                                    <option value="Slog">Slog / Cross-bat</option>
                                    <option value="Reverse">Reverse Sweep</option>
                                </optgroup>
                            </select>
                        </div>
                    )}
                </div>
            </div>

            <div>
                <label className="text-gray-400 text-sm block mb-2">How did it feel?</label>
                <div className="grid grid-cols-4 gap-2 text-xs">
                    {['Dominant', 'Solid', 'Scratchy', 'Struggled'].map(f => (
                        <button
                            key={f}
                            onClick={() => updateData('feeling', f)}
                            className={`p-2 rounded border text-center ${data.feeling === f ? 'bg-purple-600 border-purple-600 text-white' : 'border-gray-700 text-gray-500'}`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="h-full bg-gray-900 flex flex-col">
            <div className="p-4 bg-gray-800 border-b border-gray-700 flex items-center justify-between">
                <button onClick={onCancel} className="text-gray-400"><ArrowLeft /></button>
                <h2 className="text-white font-bold">Log Match</h2>
                <div className="w-6"></div>
            </div>

            {error && (
                <div className="bg-red-500/10 border border-red-500 p-4 m-4 rounded-lg text-red-500 text-sm">
                    {error}
                </div>
            )}

            <div className="flex-1 p-6 overflow-y-auto">
                {step === 1 ? renderStep1() : renderStep2()}
            </div>

            <div className="p-6 bg-gray-800 border-t border-gray-700">
                {step === 1 ? (
                    <button
                        onClick={() => setStep(2)}
                        disabled={!data.format || !data.result}
                        className="w-full bg-white text-black font-bold py-4 rounded-xl hover:bg-gray-200 disabled:opacity-50"
                    >
                        Next: Performance
                    </button>
                ) : (
                    <button
                        onClick={handleSave}
                        disabled={loading || !data.feeling}
                        className="w-full bg-green-500 text-white font-bold py-4 rounded-xl hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {loading ? 'Saving...' : <><Save size={20} /> Save to Career</>}
                    </button>
                )}
            </div>

            {
                showInjectionModal && pendingDrill && (
                    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-6 z-50 animate-fadeIn">
                        <div className="bg-gray-900 border-2 border-purple-500 rounded-2xl p-6 max-w-sm w-full relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-50">
                                <AlertTriangle size={120} className="text-purple-500/10" />
                            </div>

                            <h3 className="text-xl font-bold text-white mb-2 relative z-10">System Suggestion</h3>
                            <p className="text-gray-400 text-sm mb-6 relative z-10">
                                We noticed you struggled with the <strong>{data.shotAttempted}</strong> ({data.dismissalType}).
                            </p>

                            <div className="bg-purple-500/10 border border-purple-500/50 p-4 rounded-xl mb-6 relative z-10">
                                <h4 className="font-bold text-purple-300 text-sm uppercase mb-1">Recommended Drill</h4>
                                <p className="text-white font-bold text-lg">{pendingDrill.name}</p>
                                <p className="text-gray-400 text-xs mt-1">{pendingDrill.instructions}</p>
                            </div>

                            <div className="flex gap-3 relative z-10">
                                <button
                                    onClick={onSave}
                                    className="flex-1 py-3 bg-gray-800 text-gray-400 font-bold rounded-xl"
                                >
                                    No Thanks
                                </button>
                                <button
                                    onClick={handleConfirmInjection}
                                    className="flex-1 py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-500"
                                >
                                    Add to Plan
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default MatchLogger;
